import { useMemo, useState } from 'react';
import { useField } from 'formik';
import { influencer } from '@round/api';
import { Combobox, ComboboxCountryItem, ComboboxCountryItemType, Field, formikHelpers } from '@round/creatorbase-ui';
import { useCountries } from '@round/creatorbase-helpers/api-hooks';

const { Field: FormikField } = formikHelpers;

/** What the endpoint takes. The field refuses the rest rather than letting the create be rejected. */
const MAX_LOCATIONS = 100;

const LIMIT_MESSAGE = `Only ${MAX_LOCATIONS} countries can be targeted.`;

type CountryOption = ComboboxCountryItemType<number>;

/**
 * Countries are held as `Location` ids, and the whole country list is read once per session — so a
 * badge is a lookup by id rather than a request of its own.
 */
export const CreatorLocationField = () => {
    const { countries, isLoading } = useCountries();
    const [{ value: selectedIds }, , { setValue }] = useField<number[]>('locations');
    const [limitMessage, setLimitMessage] = useState<string | null>(null);

    const options = useMemo(() => countries.map(getCountryOption), [countries]);

    // A country the list has not arrived with yet still has to hold its place in the selection.
    const selectedOptions = useMemo(() => {
        const optionsById = new Map(options.map((option) => [option.value, option]));
        return selectedIds.map((id) => optionsById.get(id) ?? { value: id, label: String(id) });
    }, [options, selectedIds]);

    const handleValueChange = (selected: CountryOption[]) => {
        if (selected.length > MAX_LOCATIONS) {
            setLimitMessage(LIMIT_MESSAGE);
            return;
        }

        setLimitMessage(null);
        setValue(selected.map((option) => option.value));
    };

    return (
        <FormikField name="locations">
            <FormikField.Title>Creator location</FormikField.Title>
            {/* Nothing can be picked before the countries land, so the field says so by not opening. */}
            <Combobox<CountryOption, true>
                multiple
                disabled={isLoading}
                items={options}
                value={selectedOptions}
                placeholder={selectedIds.length ? undefined : 'Anywhere'}
                noItemsFound="No countries found"
                isItemEqualToValue={(option, value) => option.value === value.value}
                onValueChange={handleValueChange}
                renderItem={(item) => <ComboboxCountryItem item={item} />}
            />

            {limitMessage && <Field.Error>{limitMessage}</Field.Error>}
            <FormikField.Error name="locations" />
        </FormikField>
    );
};

const getCountryOption = (location: influencer.InfluencerLocation): CountryOption => ({
    value: location.id,
    label: location.country_name,
    countryCode: location.country_code,
});
